import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPluginUI } from 'molstar/lib/mol-plugin-ui';
import { ColorNames } from 'molstar/lib/mol-util/color/names';
import 'molstar/lib/mol-plugin-ui/skin/light.scss';
import { renderReact18 } from 'molstar/lib/mol-plugin-ui/react18';
import { PluginCommands } from 'molstar/lib/mol-plugin/commands';
import { Script } from 'molstar/lib/mol-script/script';
import { StructureSelection } from 'molstar/lib/mol-model/structure/query';
import { MolScriptBuilder as MS, MolScriptBuilder } from 'molstar/lib/mol-script/language/builder';
import { StateTransforms } from 'molstar/lib/mol-plugin-state/transforms';
import { createStructureRepresentationParams } from 'molstar/lib/mol-plugin-state/helpers/structure-representation-params';
import { Color } from "molstar/lib/mol-util/color";
import { StructureElement, StructureProperties, Unit } from 'molstar/lib/mol-model/structure';
import { CustomInteractions, InteractionsShape } from 'molstar/lib/extensions/interactions/transforms';
import { ShapeRepresentation3D } from 'molstar/lib/mol-plugin-state/transforms/representation';
import { alignAndSuperposeWithSIFTSMapping } from 'molstar/lib/mol-model/structure/structure/util/superposition-sifts-mapping';
import { Mat4 } from 'molstar/lib/mol-math/linear-algebra';
import { StateObjectCell, StateObjectRef } from 'molstar/lib/mol-state';
import { setSubtreeVisibility } from 'molstar/lib/mol-plugin/behavior/static/state';
import { getCif } from '../../ApiFunctions';
import multimerData from '../../mapa_alphafold.json';

const multimerMap = multimerData.default || multimerData;
const stemOf = (f) => (f || '').replace(/-model_v\d+\.cif$/, '');

// An AlphaFold identifier can name three different kinds of file, and each one
// lives somewhere else:
//   AF-<16 digits>          -> a multimer from mapa_alphafold.json (its own url)
//   AF-<acc>-<acc>[-...]    -> a locally predicted complex, served by getCif()
//   AF-<acc> / anything else-> the public EBI monomer model
// Level 3 lets the user pick any of them, so the loader has to branch on the
// id rather than assume the monomer.
const isMapComplexId = (id) => /^AF-\d{16}$/i.test(id || '');
const isLocalComplexId = (id) => ((id || '').match(/-/g) || []).length >= 2;

async function loadAlphaFoldData(plugin, afStructureId, uniac) {
    if (isMapComplexId(afStructureId)) {
        const rec = (uniac && Array.isArray(multimerMap[uniac]))
            ? multimerMap[uniac].find(
                r => stemOf(r.file).toUpperCase() === String(afStructureId).toUpperCase())
            : null;
        const url = (rec && rec.url)
            || `https://alphafold.ebi.ac.uk/files/${afStructureId}-model_v1.cif`;
        return plugin.builders.data.download({ url }, { state: { isGhost: true } });
    }

    if (isLocalComplexId(afStructureId)) {
        const cifString = await getCif(
            afStructureId.replace("AF-", "").replace("_", "-"));
        return plugin.builders.data.rawData({
            data: cifString, label: `${afStructureId}.cif`,
        });
    }

    // Monomer. Prefer the accession when we have it: the caller may have passed
    // a PDB id here (Level 3 overlays the PDB but renders the AF model).
    const acc = uniac || String(afStructureId || '').replace(/^AF-/i, '');
    const url = `https://alphafold.ebi.ac.uk/files/AF-${acc}-F1-model_v6.cif`;
    return plugin.builders.data.download({ url }, { state: { isGhost: true } });
}

function createLociLabelProviderGeneric(residueDataMap, formatLabelFn) {
    return {
        label: (loci) => {
            if (StructureElement.Loci.is(loci)) {
                const loc = StructureElement.Loci.getFirstLocation(loci);
                if (loc) {
                    const position = StructureProperties.residue.auth_seq_id(loc);
                    const matchingResidue = residueDataMap[position];
                    if (matchingResidue) {
                        return formatLabelFn(matchingResidue);
                    }
                }
            }
            return undefined;
        },
    };
}

// Residue.position is sometimes stored/returned accession-qualified, e.g.
// "P17612_201" or "Q9BQE5_45", instead of a bare integer (seen mainly for
// structures resolved through AlphaFold-only paths with no PDB backing).
// parseInt on a string starting with a letter returns NaN, silently
// producing an empty selection (no spheres, cartoon stays reset-white).
// Strip any non-numeric prefix so both formats resolve to the same seq id.
function toResidueSeqId(p) {
    if (typeof p === 'number') return p;
    const s = String(p ?? '');
    const m = s.match(/(\d+)\s*$/);
    return m ? parseInt(m[1], 10) : parseInt(s, 10);
}

// ──────────────────────────────────────────────────────────────────────────
// Anchor resolution for interaction lines.
// An endpoint whose atom doesn't exist in the residue (CB on a glycine, CA on a
// ligand) resolves to an empty loci, and an empty loci's bounding-sphere centre
// is the origin — that is what draws a dash off into empty space. Indexing the
// atom names once lets each endpoint fall back to a real atom, and lets pairs
// whose residue isn't in this structure be dropped instead of drawn.
// ──────────────────────────────────────────────────────────────────────────
function buildResidueLookup(structureData) {
    const atoms = new Map();    // `${auth_asym_id}|${seq}` -> Set(atom names)
    const byLabel = new Map();  // `${label_asym_id}|${seq}` -> auth_asym_id
    const bySeq = new Map();    // seq -> Set(auth_asym_id)
    if (!structureData) return { atoms, byLabel, bySeq };

    for (const unit of structureData.units) {
        if (!Unit.isAtomic(unit) || unit.elements.length === 0) continue;
        const loc = StructureElement.Location.create(structureData, unit, unit.elements[0]);
        for (let i = 0, il = unit.elements.length; i < il; i++) {
            loc.element = unit.elements[i];
            const authChain = StructureProperties.chain.auth_asym_id(loc);
            const labelChain = StructureProperties.chain.label_asym_id(loc);
            const seq = StructureProperties.residue.auth_seq_id(loc);

            const key = `${authChain}|${seq}`;
            let names = atoms.get(key);
            if (!names) { names = new Set(); atoms.set(key, names); }
            names.add(StructureProperties.atom.label_atom_id(loc));

            const labelKey = `${labelChain}|${seq}`;
            if (!byLabel.has(labelKey)) byLabel.set(labelKey, authChain);

            let chains = bySeq.get(seq);
            if (!chains) { chains = new Set(); bySeq.set(seq, chains); }
            chains.add(authChain);
        }
    }
    return { atoms, byLabel, bySeq };
}

function buildResidueAtomIndex(structureData) {
    return buildResidueLookup(structureData).atoms;
}

// -> the auth chain to use for (chainId, seq), or null when the residue is not
// in this structure at all. The chain that came with the data wins; failing
// that, it is tried as an mmCIF label chain, and finally, if exactly one chain
// carries that residue number, that chain is used.
function resolveResidueChain(lookup, chainId, seq) {
    if (chainId && lookup.atoms.has(`${chainId}|${seq}`)) return chainId;
    if (chainId) {
        const viaLabel = lookup.byLabel.get(`${chainId}|${seq}`);
        if (viaLabel) return viaLabel;
    }
    const candidates = lookup.bySeq.get(seq);
    if (candidates && candidates.size === 1) return candidates.values().next().value;
    return null;
}

// -> { atom } with the atom to anchor on (undefined = whole residue, i.e. its
// centroid, which is the sane fallback for ligands and for GLY without CB),
// or null when the residue isn't present in this structure at all.
function resolveAnchorAtom(index, chainId, seqId, preferred) {
    const atoms = index.get(`${chainId}|${seqId}`);
    if (!atoms) return null;
    for (const candidate of [preferred, 'CB', 'CA']) {
        if (candidate && atoms.has(candidate)) return { atom: candidate };
    }
    return { atom: undefined };
}

const getHeatmapColor = (value, min, max, metricKey) => {
    let normalized = (value - min) / (max - min || 1);
    if (normalized < 0.5) {
        const factor = normalized * 2;
        const r = Math.round(59 + (251 - 59) * factor);
        const g = Math.round(130 + (191 - 130) * factor);
        const b = Math.round(246 + (36 - 246) * factor);
        return (r << 16) | (g << 8) | b;
    } else {
        const factor = (normalized - 0.5) * 2;
        const r = Math.round(251 + (239 - 251) * factor);
        const g = Math.round(191 + (68 - 191) * factor);
        const b = Math.round(36 + (68 - 36) * factor);
        return (r << 16) | (g << 8) | b;
    }
};

// ──────────────────────────────────────────────────────────────────────────
// Level 14 — PDB-vs-PDB rewiring mode: overpaint each residue by its rewiring
// value (0 = contacts conserved, 1 = fully rewired) using the same 3-stop
// gradient as the PathfinderPage legend (#dbeafe -> #7c3aed -> #b91c1c).
// ──────────────────────────────────────────────────────────────────────────
function rewiringHex(t) {
    const c = Math.max(0, Math.min(1, Number(t) || 0));
    const lerp = (a, b, f) => Math.round(a + (b - a) * f);
    let r, g, b;
    if (c < 0.5) {
        const f = c * 2;
        r = lerp(0xdb, 0x7c, f); g = lerp(0xea, 0x3a, f); b = lerp(0xfe, 0xed, f);
    } else {
        const f = (c - 0.5) * 2;
        r = lerp(0x7c, 0xb9, f); g = lerp(0x3a, 0x1c, f); b = lerp(0xed, 0x1c, f);
    }
    return (r << 16) | (g << 8) | b;
}

async function applyRewiringColoring(plugin, cartoonRef, datatable) {
    if (!cartoonRef || !datatable) return;

    const reprParamsReset = createStructureRepresentationParams(plugin, undefined, {
        type: 'cartoon',
        color: 'uniform',
        colorParams: { value: Color(0xEEEEEE) }
    });
    await plugin.build().to(cartoonRef).update(reprParamsReset).commit();

    const language = 'mol-script';
    const layers = datatable.map((row) => {
        const residueId = toResidueSeqId(row.col1);
        const ch = row.chain || 'A';
        const rw = Number(row.rewiring);
        if (isNaN(residueId) || Number.isNaN(rw)) return null;
        return {
            script: Script(
                `(sel.atom.atom-groups
                     :chain-test (= atom.auth_asym_id ${ch})
                     :residue-test (set.has (set ${residueId}) atom.auth_seq_id)
                  )`,
                language
            ),
            color: Color(rewiringHex(rw)),
            clear: false
        };
    }).filter(l => l !== null);

    if (layers.length > 0) {
        const clearLayer = {
            script: Script(
                `(sel.atom.atom-groups :residue-test (= atom.auth_seq_id atom.auth_seq_id))`,
                language
            ),
            color: Color(0xEEEEEE),
            clear: true
        };
        await plugin.build()
            .to(cartoonRef)
            .apply(StateTransforms.Representation.OverpaintStructureRepresentation3DFromScript, { layers: [clearLayer, ...layers] })
            .commit();
    }
}

async function transform(s, matrix, coordinateSystem, plugin) {
    const r = StateObjectRef.resolveAndCheck(plugin.state.data, s);
    if (!r) return;
    const o = plugin.state.data.selectQ(q => q.byRef(r.transform.ref).subtree().withTransformer(StateTransforms.Model.TransformStructureConformation))[0];
    const transformMatrix = coordinateSystem && !Mat4.isIdentity(coordinateSystem.matrix)
        ? Mat4.mul(Mat4(), coordinateSystem.matrix, matrix)
        : matrix;
    const params = { transform: { name: 'matrix', params: { data: transformMatrix, transpose: false } } };
    const b = o
        ? plugin.state.data.build().to(o).update(params)
        : plugin.state.data.build().to(s).insert(StateTransforms.Model.TransformStructureConformation, params, { tags: 'SuperpositionTransform' });
    await plugin.runTask(plugin.state.data.updateTree(b));
}

async function loadPdbForSuperpositionTrigger(plugin, pdbIdToLoad) {
    try {
        const pdbData = await plugin.builders.data.download(
            { url: `https://www.ebi.ac.uk/pdbe/entry-files/${pdbIdToLoad.toLowerCase()}_updated.cif` },
            { state: { isGhost: true } }
        );
        const pdbTraj = await plugin.builders.structure.parseTrajectory(pdbData, 'mmcif');
        const preset = await plugin.builders.structure.hierarchy.applyPreset(pdbTraj, 'default');
        const cartoonPDB = preset.representation.representations.polymer;
        const reprParams = createStructureRepresentationParams(plugin, undefined, {
            type: 'cartoon',
            color: 'uniform',
            colorParams: { value: ColorNames.gray }
        });
        await plugin.build().to(cartoonPDB).update(reprParams).commit();
        const input = plugin.managers.structure.hierarchy.behaviors.selection.value.structures;
        const structures = input.map(s => s.cell?.obj?.data);
        const traceOnly = true;
        let { entries, failedPairs, zeroOverlapPairs } = alignAndSuperposeWithSIFTSMapping(structures, { traceOnly, applyTestIndex: [1] });
        const coordinateSystem = input[0]?.cell.obj?.data?.state?.coordinateSystem;
        let rmsd = 0;
        for (const xform of entries) {
            await transform(input[xform.other].cell, xform.transform.bTransform, coordinateSystem, plugin);
            rmsd += xform.transform.rmsd;
        }
        rmsd /= Math.max(entries.length - 1, 1);
        PluginCommands.Toast.Show(plugin, {
            title: 'Superposition',
            message: entries.length === 0 ? 'No superposition between given structures' : 'RMSD (Å) ' + rmsd.toFixed(2),
            key: 'toast-custom',
        });
        return pdbData?.ref ?? null;   // ref of the overlay subtree, so it can be removed
    } catch (error) {
        console.error(`Loading PDB ${pdbIdToLoad} for superposition failed:`, error);
        return null;
    }
}


// ── Level 1 — PyMOL-like path styling ──
// Carbons of the path residues get this colour; N/O/S keep the CPK palette,
// which is what makes the sticks read like a PyMOL `show sticks` + `color`.
const PATH_CARBON = 0xF08000;   // orange carbons
const PATH_LABEL_COLOR = 0x111111;   // dark text (viewer background is white)
const PATH_LINE_COLOR = 0xF0E68C;    // dashed connector between consecutive nodes
const PATH_LINE_ATOM = 'CA';         // atom the connectors are anchored on

// Mol*'s own viewport buttons live in a 32px column inset by 10px from the top
// right corner (`.msp-viewport-controls` in the skin), so the labels toggle sits
// to their left. Colours/metrics are the light skin's `.msp-form-control`
// values, hardcoded because the skin compiles to plain CSS scoped under
// `.msp-plugin` — a button outside that subtree can't inherit them.
const MSP_CONTROL_SPACING = 10;
const MSP_CONTROL_COLUMN = 32;
const MSP_BUTTON_STYLE = {
    position: 'absolute',
    top: MSP_CONTROL_SPACING,
    right: MSP_CONTROL_SPACING + MSP_CONTROL_COLUMN + 6,
    zIndex: 10,
    height: 32,
    padding: '0 10px',
    border: 'none',
    borderRadius: 0,
    background: '#F3F2EE',
    color: '#332B1F',
    fontFamily: '"Helvetica Neue", "Segoe UI", Helvetica, "Source Sans Pro", Arial, sans-serif',
    fontSize: 14,
    lineHeight: '30px',
    boxShadow: 'none',
    cursor: 'pointer',
};

// Delete state subtrees created as named components (e.g. the Hubs sticks).
// Representations added on top of a component are children of it, so removing
// the component ref takes the sticks with it.
async function removeComponentsByLabel(plugin, labels) {
    try {
        const refs = [];
        plugin.state.data.cells.forEach((cell, ref) => {
            const tag = cell?.params?.values?.label || cell?.obj?.label || '';
            if (typeof tag === 'string' && labels.includes(tag)) refs.push(ref);
        });
        for (const ref of refs) await plugin.build().delete(ref).commit();
    } catch (e) {
        console.warn('Component cleanup warning:', e);
    }
}

const isLigand = (col1) => typeof col1 === 'string' && /^[A-Z]{2,3}$/.test(col1.trim());
async function applyLevel2Coloring(plugin, cartoonRef, datatable, centralityMetric) {
    if (!cartoonRef) return;

    // Switching Hubs -> Degree/Betweenness must not leave the hub sticks behind:
    // the component is rebuilt from scratch on every call, so drop it first.
    await removeComponentsByLabel(plugin, ['SPM Hubs']);

    const reprParamsReset = createStructureRepresentationParams(plugin, undefined, {
        type: 'cartoon',
        color: 'uniform',
        colorParams: { value: Color(0xEEEEEE) }
    });
    await plugin.build().to(cartoonRef).update(reprParamsReset).commit();

    const metricKey = centralityMetric || 'col2';

    const filteredDatatable = datatable.filter((row) => row && !isLigand(row.col1) && row.col1 !== '__summary__');


    if (metricKey === 'col5') {
        // ── Hubs ──
        const HUB_COLOR = 0xE53935;
        const language = 'mol-script';

        const hubRows = filteredDatatable.filter((row) => row.col5 === 'Yes');

        const hubLayers = hubRows.map((row) => {
            const residueId = toResidueSeqId(row.col1);
            const ch = row.chain;
            if (isNaN(residueId)) return null;
            return {
                script: Script(
                    `(sel.atom.atom-groups
                         :chain-test (= atom.auth_asym_id ${ch})
                         :residue-test (set.has (set ${residueId}) atom.auth_seq_id)
                      )`,
                    language
                ),
                color: Color(HUB_COLOR),
                clear: false,
            };
        }).filter(l => l !== null);

        // The base-colour update above doesn't clear existing overpaint, so a
        // full-selection clear layer goes first — otherwise the heatmap colours
        // from the previous metric stay on the cartoon underneath the hubs.
        const clearLayer = {
            script: Script(
                `(sel.atom.atom-groups :residue-test (= atom.auth_seq_id atom.auth_seq_id))`,
                language
            ),
            color: Color(0xEEEEEE),
            clear: true
        };
        await plugin.build()
            .to(cartoonRef)
            .apply(StateTransforms.Representation.OverpaintStructureRepresentation3DFromScript, { layers: [clearLayer, ...hubLayers] })
            .commit();

        if (hubLayers.length > 0) {

            const structRef = plugin.managers.structure.hierarchy.current.structures[0]?.cell;
            if (structRef) {
                const orExpressions = hubRows.map((row) => {
                    const residueId = toResidueSeqId(row.col1);
                    const ch = row.chain;
                    if (isNaN(residueId)) return null;
                    return MolScriptBuilder.struct.generator.atomGroups({
                        'chain-test': MolScriptBuilder.core.rel.eq([
                            MolScriptBuilder.struct.atomProperty.macromolecular.auth_asym_id(), ch,
                        ]),
                        'residue-test': MolScriptBuilder.core.rel.eq([
                            MolScriptBuilder.struct.atomProperty.macromolecular.auth_seq_id(), residueId,
                        ]),
                    });
                }).filter(e => e !== null);

                if (orExpressions.length > 0) {
                    const combined = MolScriptBuilder.struct.combinator.merge(orExpressions);
                    const sel = await plugin.builders.structure.tryCreateComponentFromExpression(
                        structRef, combined, 'spm-hubs', { label: 'SPM Hubs' }
                    );
                    if (sel) {
                        await plugin.builders.structure.representation.addRepresentation(sel, {
                            type: 'ball-and-stick',
                            color: 'uniform',
                            colorParams: { value: Color(HUB_COLOR) },
                            sizeParams: { value: 0.6 },
                        });
                    }
                }
            }
        }
    } else {
        // ── Heatmap (Degree, Betweenness, etc.) ──
        const values = filteredDatatable.map(d => parseFloat(d[metricKey])).filter(v => !isNaN(v));

        const minVal = Math.min(...values);
        const maxVal = Math.max(...values);
        const language = 'mol-script';

        const layers = filteredDatatable.map((row) => {
            const residueId = toResidueSeqId(row.col1);
            const val = parseFloat(row[metricKey]);
            const ch = row.chain;
            if (isNaN(residueId) || isNaN(val)) return null;
            return {
                script: Script(
                    `(sel.atom.atom-groups
                         :chain-test (= atom.auth_asym_id ${ch})
                         :residue-test (set.has (set ${residueId}) atom.auth_seq_id)
                      )`,
                    language
                ),
                color: Color(getHeatmapColor(val, minVal, maxVal, metricKey)),
                clear: false
            };
        }).filter(l => l !== null);

        if (layers.length > 0) {

            const clearLayer = {
                script: Script(
                    `(sel.atom.atom-groups :residue-test (= atom.auth_seq_id atom.auth_seq_id))`,
                    language
                ),
                color: Color(0xEEEEEE),
                clear: true
            };
            await plugin.build()
                .to(cartoonRef)
                .apply(StateTransforms.Representation.OverpaintStructureRepresentation3DFromScript, { layers: [clearLayer, ...layers] })
                .commit();
        }
    }
}

// ──────────────────────────────────────────────────────────────────────────
// Level 5 — pLDDT colouring (AlphaFold confidence) on the cartoon.
// ──────────────────────────────────────────────────────────────────────────
async function applyLevel5Plddt(plugin, cartoonRef) {
    if (!cartoonRef) return;
    const params = createStructureRepresentationParams(plugin, undefined, {
        type: 'cartoon',
        color: 'uncertainty',
        colorParams: {
            domain: [0, 100],
            list: { colors: [Color(0x0984e3), Color(0x74b9ff), Color(0xf1c40f), Color(0xff7675)] },
            size: "uniform",
        }
    });
    await plugin.build().to(cartoonRef).update(params).commit();

    // Wipe any leftover class-colour overpaint so pLDDT shows cleanly. The base
    // colour update above does NOT remove overpaint layers, so a full-selection
    // clear:true layer is needed (same pattern used when applying class colours).
    await plugin.build()
        .to(cartoonRef)
        .apply(StateTransforms.Representation.OverpaintStructureRepresentation3DFromScript, {
            layers: [{
                script: Script(
                    `(sel.atom.atom-groups :residue-test (= atom.auth_seq_id atom.auth_seq_id))`,
                    'mol-script'
                ),
                color: Color(0xEEEEEE),
                clear: true
            }]
        })
        .commit();
}

// ──────────────────────────────────────────────────────────────────────────
// Level 5 — Structure-difference classes: colour each residue by its
// structure_class (rigid core / allosteric hinge / transient). Numbering is
// canonical (AlphaFold = UniProt), so no structureMap is needed.
// ──────────────────────────────────────────────────────────────────────────
const CLASS_COLOR = {
    rigid:     0x1e3a8a,   // dark blue  — rigid core (consensus contacts)
    hinge:     0x7c3aed,   // purple     — allosteric hinge (rewired)
    transient: 0xf59e0b,   // orange     — flexible / transient contacts
};

async function applyClassColoring(plugin, cartoonRef, datatable) {
    if (!cartoonRef || !datatable) return;

    const reprParamsReset = createStructureRepresentationParams(plugin, undefined, {
        type: 'cartoon',
        color: 'uniform',
        colorParams: { value: Color(0xEEEEEE) }
    });
    await plugin.build().to(cartoonRef).update(reprParamsReset).commit();

    const language = 'mol-script';
    const layers = datatable.map((row) => {
        const residueId = toResidueSeqId(row.col1);
        const ch = row.chain || 'A';
        const col = CLASS_COLOR[row.structure_class];
        if (isNaN(residueId) || col === undefined) return null;
        return {
            script: Script(
                `(sel.atom.atom-groups
                     :chain-test (= atom.auth_asym_id ${ch})
                     :residue-test (set.has (set ${residueId}) atom.auth_seq_id)
                  )`,
                language
            ),
            color: Color(col),
            clear: false
        };
    }).filter(l => l !== null);

    if (layers.length > 0) {
        const clearLayer = {
            script: Script(
                `(sel.atom.atom-groups :residue-test (= atom.auth_seq_id atom.auth_seq_id))`,
                language
            ),
            color: Color(0xEEEEEE),
            clear: true
        };
        await plugin.build()
            .to(cartoonRef)
            .apply(StateTransforms.Representation.OverpaintStructureRepresentation3DFromScript, { layers: [clearLayer, ...layers] })
            .commit();
    }
}

// ──────────────────────────────────────────────────────────────────────────
// Level 9 — PTM sites mode: overpaint each modified residue with the per-row
// colour supplied by the caller (PathfinderPage builds `color` from PTM_COLOR_MAP).
// Same overpaint mechanism as applyClassColoring, but the colour is data-driven
// (row.color hex) instead of a fixed category → palette lookup.
// ──────────────────────────────────────────────────────────────────────────
async function applyPtmColoring(plugin, cartoonRef, datatable) {
    if (!cartoonRef || !datatable) return;

    const reprParamsReset = createStructureRepresentationParams(plugin, undefined, {
        type: 'cartoon',
        color: 'uniform',
        colorParams: { value: Color(0xEEEEEE) }
    });
    await plugin.build().to(cartoonRef).update(reprParamsReset).commit();

    const language = 'mol-script';
    const layers = datatable.map((row) => {
        const residueId = toResidueSeqId(row.col1);
        const ch = row.chain || 'A';
        const hex = typeof row.color === 'string' ? parseInt(row.color.replace('#', ''), 16) : row.color;
        if (isNaN(residueId) || hex === undefined || Number.isNaN(hex)) return null;
        return {
            script: Script(
                `(sel.atom.atom-groups
                     :chain-test (= atom.auth_asym_id ${ch})
                     :residue-test (set.has (set ${residueId}) atom.auth_seq_id)
                  )`,
                language
            ),
            color: Color(hex),
            clear: false
        };
    }).filter(l => l !== null);

    if (layers.length > 0) {
        const clearLayer = {
            script: Script(
                `(sel.atom.atom-groups :residue-test (= atom.auth_seq_id atom.auth_seq_id))`,
                language
            ),
            color: Color(0xEEEEEE),
            clear: true
        };
        await plugin.build()
            .to(cartoonRef)
            .apply(StateTransforms.Representation.OverpaintStructureRepresentation3DFromScript, { layers: [clearLayer, ...layers] })
            .commit();
    }
}

// ──────────────────────────────────────────────────────────────────────────
// Level 3 — Variants mode: paint the listed residues as red spacefill spheres.
// Cleans up any previous "Variant residues" components so re-running with a
// new positions/chain list (e.g. filter change) actually updates the viewer.
// ──────────────────────────────────────────────────────────────────────────
async function applyLevel3Variants(plugin, cartoonRef, positions, chain) {
    if (!cartoonRef) return;

    // Reset cartoon to neutral white
    const reprParamsReset = createStructureRepresentationParams(plugin, undefined, {
        type: 'cartoon',
        color: 'uniform',
        colorParams: { value: Color(0xFAFAFA) }
    });
    await plugin.build().to(cartoonRef).update(reprParamsReset).commit();

    // Remove any previously created "Variant residues" component (so toggling
    // the lateral filter actually replaces the spheres instead of stacking).
    try {
        const stateRefs = [];
        plugin.state.data.cells.forEach((cell, ref) => {
            const tag = cell?.params?.values?.label || cell?.obj?.label || '';
            if (typeof tag === 'string' && tag === 'Variant residues') {
                stateRefs.push(ref);
            }
        });
        for (const ref of stateRefs) {
            await plugin.build().delete(ref).commit();
        }
    } catch (e) {
        // best-effort cleanup
        console.warn('Level 3 cleanup warning:', e);
    }

    if (!positions?.length || !chain?.length) return;

    const dta = plugin.managers.structure.hierarchy.current.structures[0]?.cell.obj?.data;
    if (!dta) return;

    const selection = Script.getStructureSelection(Q => Q.struct.generator.atomGroups({
        'chain-test':   Q.core.set.has([Q.set(...chain), Q.ammp('auth_asym_id')]),
        'residue-test': Q.core.set.has([Q.set(...positions.map(toResidueSeqId)), Q.ammp('auth_seq_id')]),
        'atom-test':    Q.core.set.has([Q.set('CA'), Q.ammp('label_atom_id')]),
        'group-by':     Q.struct.atomProperty.macromolecular.residueKey(),
    }), dta);

    const reprParams = createStructureRepresentationParams(plugin, undefined, {
        type: 'spacefill',
        color: 'uniform',
        colorParams: { value: Color(0xc93c20) },
        size: 'physical',
        sizeParams: { scale: 1 }
    });

    await plugin.build()
        .to(cartoonRef)
        .apply(StateTransforms.Model.StructureSelectionFromExpression, {
            label: 'Variant residues',
            expression: selection.expression || selection
        })
        .apply(StateTransforms.Representation.StructureRepresentation3D, reprParams)
        .commit();
}

// ──────────────────────────────────────────────────────────────────────────
// Level 11 — Co-Pathogenicity: paint query residues as red spacefill spheres
// and partner residues as yellow spacefill spheres. Uses PAIRED (position,
// chain) selection (one atomGroup per residue, merged) so a partner residue
// number can never bleed onto the query chain (and vice-versa). Cleans up its
// own components so re-running with a new selection replaces the spheres.
// ──────────────────────────────────────────────────────────────────────────
async function applyLevel11Variants(plugin, cartoonRef, queryPositions, queryChain, partnerPositions, partnerChain) {
    if (!cartoonRef) return;

    // Reset cartoon to neutral
    const reprParamsReset = createStructureRepresentationParams(plugin, undefined, {
        type: 'cartoon',
        color: 'uniform',
        colorParams: { value: Color(0xFAFAFA) }
    });
    await plugin.build().to(cartoonRef).update(reprParamsReset).commit();

    // Remove previously created Level 11 components (replace, don't stack)
    const L11_LABELS = ['L11 query residues', 'L11 partner residues'];
    try {
        const refs = [];
        plugin.state.data.cells.forEach((cell, ref) => {
            const tag = cell?.params?.values?.label || cell?.obj?.label || '';
            if (typeof tag === 'string' && L11_LABELS.includes(tag)) refs.push(ref);
        });
        for (const ref of refs) await plugin.build().delete(ref).commit();
    } catch (e) {
        console.warn('Level 11 cleanup warning:', e);
    }

    const dta = plugin.managers.structure.hierarchy.current.structures[0]?.cell.obj?.data;
    if (!dta) return;

    const groups = [
        { pos: queryPositions,   chain: queryChain,   color: 0xc93c20, label: 'L11 query residues' },
        { pos: partnerPositions, chain: partnerChain, color: 0xf0e68c, label: 'L11 partner residues' },
    ];

    for (const g of groups) {
        if (!g.pos?.length || !g.chain?.length) continue;

        // one atomGroup per (residue, chain) pair → merged selection (paired, not set-product)
        const selection = Script.getStructureSelection(Q => Q.struct.combinator.merge(
            g.pos.map((p, i) => Q.struct.generator.atomGroups({
                'chain-test':   Q.core.rel.eq([Q.ammp('auth_asym_id'), g.chain[i]]),
                'residue-test': Q.core.rel.eq([Q.ammp('auth_seq_id'), toResidueSeqId(p)]),
                'atom-test':    Q.core.set.has([Q.set('CA'), Q.ammp('label_atom_id')]),
                'group-by':     Q.struct.atomProperty.macromolecular.residueKey(),
            }))
        ), dta);

        const reprParams = createStructureRepresentationParams(plugin, undefined, {
            type: 'spacefill',
            color: 'uniform',
            colorParams: { value: Color(g.color) },
            size: 'physical',
            sizeParams: { scale: 1 }
        });

        await plugin.build()
            .to(cartoonRef)
            .apply(StateTransforms.Model.StructureSelectionFromExpression, {
                label: g.label,
                expression: selection.expression || selection
            })
            .apply(StateTransforms.Representation.StructureRepresentation3D, reprParams)
            .commit();
    }
}

const MolstarViewerPath = ({ pdbId, uniac, level, chain, positions, partnerPositions, partnerChain, datatable, centralityMetric, colorBy, superpose, overlayPdbId, afId, connectSourceTarget = true }) => {

    const viewerRef = useRef(null);
    const pluginInstanceRef = useRef(null);
    const structureLoadedRef = useRef(false);
    const cartoonRefStore = useRef(null);
    const overlayRefStore = useRef(null);   // ref of the loaded PDB overlay (null = none)
    const labelReprRef = useRef(null);      // state ref of the Level 1 residue labels

    // Level 1 labels are on by default; the toggle below hides/shows the whole
    // label subtree instead of rebuilding it, so switching is instant.
    const [showLabels, setShowLabels] = useState(true);
    const [labelsBtnHover, setLabelsBtnHover] = useState(false);
    const showLabelsRef = useRef(true);

    useEffect(() => {
        showLabelsRef.current = showLabels;
        const plugin = pluginInstanceRef.current;
        if (!plugin || !labelReprRef.current) return;
        setSubtreeVisibility(plugin.state.data, labelReprRef.current, !showLabels);
    }, [showLabels]);

    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            if (!viewerRef.current || pluginInstanceRef.current) return;

            const plugin = await createPluginUI({
                target: viewerRef.current,
                layoutIsExpanded: false,
                layoutShowControls: false,
                layoutShowRemoteState: false,
                layoutShowSequence: true,
                layoutShowLog: false,
                layoutShowLeftPanel: false,
                viewportShowExpand: false,
                viewportShowSelectionMode: false,
                viewportShowAnimation: false,
                render: renderReact18,
            });

            if (cancelled) { plugin.dispose(); return; }
            pluginInstanceRef.current = plugin;

            try {
                const isAlphaFold = (level !== '14') && ((level === '5') || (colorBy === 'class') || (pdbId && pdbId.toString().toUpperCase().startsWith('AF-')));

                // Level 3 renders the AlphaFold model and overlays the PDB, so the
                // structure to load is `afId` when the caller supplied one. Falling
                // back to `pdbId` keeps every other level unchanged.
                const afStructureId = (level === '5' && afId) ? afId : pdbId;

                let data;
                // isMapComplexId is checked independently of isAlphaFold to match
                // the previous behaviour: a mapa_alphafold stem always loads from
                // the map, whatever the level.
                if (isMapComplexId(afStructureId) || isAlphaFold) {
                    data = await loadAlphaFoldData(plugin, afStructureId, uniac);
                } else {
                    data = await plugin.builders.data.download(
                        { url: `https://www.ebi.ac.uk/pdbe/entry-files/${pdbId.toLowerCase()}_updated.cif` },
                        { state: { isGhost: true } }
                    );
                }

                const trajectory = await plugin.builders.structure.parseTrajectory(data, 'mmcif');
                const structure = await plugin.builders.structure.hierarchy.applyPreset(trajectory, 'default');

                const renderer = plugin.canvas3d.props.renderer;
                PluginCommands.Canvas3D.SetSettings(plugin, {
                    settings: { renderer: { ...renderer, backgroundColor: ColorNames.white } }
                });

                const dta = plugin.managers.structure.hierarchy.current.structures[0]?.cell.obj?.data;
                if (!dta) return;

                const cartoon = structure.representation.representations.polymer;

                if (level === "1") {
                    const reprParamsStructureResetColor = createStructureRepresentationParams(plugin, undefined, {
                        color: 'uniform',
                        colorParams: { value: Color("0XFAFAFA") }
                    });
                    await plugin.build().to(cartoon).update(reprParamsStructureResetColor).commit();

                    const structRef = plugin.managers.structure.hierarchy.current.structures[0]?.cell;
                    const structureData = structRef?.obj?.data;

                    if (structRef && structureData && positions?.length) {
                        const lookup = buildResidueLookup(structureData);

                        // Resolve every node against the structure that is actually
                        // loaded. `chain` may be shorter than `positions`, may be a
                        // single id for the whole path, and may be an mmCIF label
                        // chain rather than the auth chain — all of which used to end
                        // up drawing on whatever chain the residue number hit first.
                        const chainAt = (i) => (Array.isArray(chain) ? chain[i] : chain);
                        const nodes = positions.map((pos, i) => {
                            const seq = toResidueSeqId(pos);
                            if (Number.isNaN(seq)) return null;
                            const wanted = chainAt(i);
                            const resolved = resolveResidueChain(lookup, wanted, seq);
                            if (!resolved) {
                                console.warn(`Path node ${wanted ?? '?'}/${seq} is not in this structure; skipped.`);
                                return null;
                            }
                            if (wanted && resolved !== wanted) {
                                console.warn(`Path node ${wanted}/${seq} not found on chain ${wanted}; drawn on ${resolved}.`);
                            }
                            return { seq, chain: resolved };
                        });

                        const present = nodes.filter(Boolean);
                        const residueExpression = (node) => MolScriptBuilder.struct.generator.atomGroups({
                            'chain-test': MolScriptBuilder.core.rel.eq([
                                MolScriptBuilder.struct.atomProperty.macromolecular.auth_asym_id(), node.chain,
                            ]),
                            'residue-test': MolScriptBuilder.core.rel.eq([
                                MolScriptBuilder.struct.atomProperty.macromolecular.auth_seq_id(), node.seq,
                            ]),
                        });

                        if (present.length > 0) {
                            const combined = MolScriptBuilder.struct.combinator.merge(present.map(residueExpression));

                            // Overpaint from a bundle rather than from an interpolated
                            // mol-script string: a chain id that isn't a bare
                            // identifier (a number, say) is parsed as a literal there
                            // and silently matches nothing.
                            try {
                                const selection = Script.getStructureSelection(() => combined, structureData);
                                const loci = StructureSelection.toLociWithCurrentUnits(selection);
                                if (!StructureElement.Loci.isEmpty(loci)) {
                                    await plugin.build()
                                        .to(cartoon)
                                        .apply(StateTransforms.Representation.OverpaintStructureRepresentation3DFromBundle, {
                                            layers: [{
                                                bundle: StructureElement.Bundle.fromLoci(loci),
                                                color: Color(0xf0e68c),
                                                clear: false,
                                            }],
                                        })
                                        .commit();
                                }
                            } catch (err) {
                                console.warn('Path overpaint fell back to mol-script:', err);
                                await plugin.build()
                                    .to(cartoon)
                                    .apply(StateTransforms.Representation.OverpaintStructureRepresentation3DFromScript, {
                                        layers: present.map((node) => ({
                                            script: Script(
                                                `(sel.atom.atom-groups
                                                     :chain-test (= atom.auth_asym_id ${node.chain})
                                                     :residue-test (set.has (set ${node.seq}) atom.auth_seq_id)
                                                  )`,
                                                'mol-script'
                                            ),
                                            color: Color(0xf0e68c),
                                            clear: false,
                                        })),
                                    })
                                    .commit();
                            }

                            const sel = await plugin.builders.structure.tryCreateComponentFromExpression(
                                structRef, combined, 'path-residues', { label: 'Path Residues (Ball & Stick)' }
                            );
                            if (sel) {
                                // (a) Sticks with element colouring: carbons in PATH_CARBON,
                                // heteroatoms in CPK (N blue, O red, S yellow). aspectRatio 1
                                // removes the balls, leaving pure PyMOL-style sticks.
                                await plugin.builders.structure.representation.addRepresentation(sel, {
                                    type: 'ball-and-stick',
                                    typeParams: {
                                        sizeFactor: 0.22,      // stick thickness
                                        aspectRatio: 1,        // 1 = no spheres at the atoms
                                        ignoreHydrogens: true,
                                    },
                                    color: 'element-symbol',
                                    colorParams: {
                                        // Mol* >= 3.30: carbonColor is a mapped param. On older
                                        // builds this key is ignored and carbons fall back to the
                                        // default grey — use { carbonByChainId: false } there.
                                        carbonColor: { name: 'uniform', params: { value: Color(PATH_CARBON) } },
                                        saturation: 0,
                                        lightness: 0,
                                    },
                                });

                                // (b) One residue label per path node (PyMOL `label ... resn+resi`).
                                // Text is generated by Mol* as "ASP 79"; it can't be customised on
                                // this representation — the MVS `custom-label` extension is the
                                // route for arbitrary text.
                                const labelRepr = await plugin.builders.structure.representation.addRepresentation(sel, {
                                    type: 'label',
                                    typeParams: {
                                        level: 'residue',
                                        attachment: 'middle-left',
                                        sizeFactor: 1.1,          // text size
                                        background: false,
                                        borderWidth: 0.2,         // outline keeps it readable over the cartoon
                                        borderColor: Color(0xFFFFFF),
                                        tether: false,
                                        offsetZ: 1.5,             // nudge the text towards the camera
                                    },
                                    color: 'uniform',
                                    colorParams: { value: Color(PATH_LABEL_COLOR) },
                                });

                                // Keep the ref so the toggle can hide/show the labels,
                                // and honour the current toggle state on (re)load.
                                labelReprRef.current = labelRepr?.ref ?? null;
                                if (labelReprRef.current && !showLabelsRef.current) {
                                    setSubtreeVisibility(plugin.state.data, labelReprRef.current, true);
                                }
                            }
                        }

                        // ── Connectors between consecutive path nodes ──
                        // measurements.add('distance', ...) proved unreliable here, so
                        // the path uses the same mechanism as MolstarViewer's contact
                        // lines: a CustomInteractions node (one entry per consecutive
                        // pair) rendered through InteractionsShape as dashed cylinders.
                        // `kinds` is left at its default because the param was renamed
                        // across Mol* versions; the default already covers 'unknown'.
                        const structureRef = structRef.transform?.ref;
                        const pathInteractions = [];
                        // A two-node path is just source and target: the single leg
                        // between them is the interaction itself, which callers can
                        // turn off with connectSourceTarget={false}.
                        const skipEndpointLeg = !connectSourceTarget && nodes.length === 2;
                        for (let i = 0; i < nodes.length - 1 && !skipEndpointLeg; i++) {
                            const a = nodes[i];
                            const b = nodes[i + 1];
                            if (!a || !b) continue;   // a node missing from the structure breaks that leg

                            // A ligand node has no CA, so anchoring blindly on
                            // PATH_LINE_ATOM would send the dash to the origin.
                            const anchorA = resolveAnchorAtom(lookup.atoms, a.chain, a.seq, PATH_LINE_ATOM);
                            const anchorB = resolveAnchorAtom(lookup.atoms, b.chain, b.seq, PATH_LINE_ATOM);
                            if (!anchorA || !anchorB) continue;

                            pathInteractions.push({
                                kind: 'unknown',
                                aStructureRef: structureRef,
                                a: {
                                    auth_asym_id: a.chain,
                                    auth_seq_id: a.seq,
                                    ...(anchorA.atom ? { label_atom_id: anchorA.atom } : {}),
                                },
                                bStructureRef: structureRef,
                                b: {
                                    auth_asym_id: b.chain,
                                    auth_seq_id: b.seq,
                                    ...(anchorB.atom ? { label_atom_id: anchorB.atom } : {}),
                                },
                                description: `${a.chain}/${a.seq} \u2192 ${b.chain}/${b.seq}`,
                            });
                        }

                        if (structureRef && pathInteractions.length > 0) {
                            try {
                                const updateLines = plugin.build();
                                updateLines
                                    .toRoot()
                                    .apply(
                                        CustomInteractions,
                                        { interactions: pathInteractions },
                                        { dependsOn: [structureRef], label: 'Path connectors' }
                                    )
                                    .apply(InteractionsShape, {
                                        styles: {
                                            unknown: {
                                                color: Color(PATH_LINE_COLOR),
                                                style: 'dashed',
                                                radius: 0.11,
                                            },
                                        },
                                    })
                                    .apply(ShapeRepresentation3D);
                                await updateLines.commit();
                            } catch (err) {
                                console.warn('Path connectors failed:', err);
                            }
                        }
                    }

                } else if (level === "3" && positions?.length && chain?.length) {
                    // ── Level 3: Variants mode — red spacefill spheres ──
                    // Delegated to applyLevel3Variants, also called from the
                    // dedicated useEffect below whenever positions or chain
                    // change (lateral filter).
                    cartoonRefStore.current = cartoon;
                    await applyLevel3Variants(plugin, cartoon, positions, chain);
                } else if (level === "11") {
                    // ── Level 11: Co-Pathogenicity — query red + partner yellow (paired) ──
                    cartoonRefStore.current = cartoon;
                    await applyLevel11Variants(plugin, cartoon, positions, chain, partnerPositions, partnerChain);
                } else if (level === "12" && positions?.length && chain?.length) {
                    // ── Level 12: VUS residues as red spheres on the pinned structure ──
                    cartoonRefStore.current = cartoon;
                    await applyLevel3Variants(plugin, cartoon, positions, chain);
                } else if (level === "13" && positions?.length && chain?.length) {
                    // ── Level 13: user's triage variants as red spheres ──
                    cartoonRefStore.current = cartoon;
                    await applyLevel3Variants(plugin, cartoon, positions, chain);
                } else if (level === "2" && datatable && datatable.length > 0) {
                    cartoonRefStore.current = cartoon;
                    await applyLevel2Coloring(plugin, cartoon, datatable, centralityMetric);

                } else if (level === "5") {
                    cartoonRefStore.current = cartoon;

                    // colour the AlphaFold model — by structure class, or by pLDDT (default)
                    if (colorBy === 'class' && datatable && datatable.length > 0) {
                        await applyClassColoring(plugin, cartoon, datatable);
                    } else {
                        await applyLevel5Plddt(plugin, cartoon);
                    }

                    plugin.managers.lociLabels.addProvider({
                        label: (loci) => {
                            if (StructureElement.Loci.is(loci)) {
                                const loc = StructureElement.Loci.getFirstLocation(loci);
                                if (loc) {
                                    const bFactor = StructureProperties.atom.B_iso_or_equiv(loc);
                                    return `pLDDT: ${bFactor}`;
                                }
                            }
                            return undefined;
                        }
                    });

                    // Superposition is no longer automatic in the single-viewer mode:
                    // legacy callers (no colorBy) keep auto-superposing; the new mode
                    // loads/removes the overlay via the dedicated effect below (button).
                    if (!colorBy) {
                        overlayRefStore.current = await loadPdbForSuperpositionTrigger(plugin, pdbId);
                    }
                } else if (level === "14") {
                    // Level 14: PDB-vs-PDB — structure A is primary; colour by
                    // rewiring heatmap or by structure class (default).
                    cartoonRefStore.current = cartoon;
                    if (colorBy === 'rewiring' && datatable && datatable.length > 0) {
                        await applyRewiringColoring(plugin, cartoon, datatable);
                    } else if (datatable && datatable.length > 0) {
                        await applyClassColoring(plugin, cartoon, datatable);
                    }
                } else if (level === "9") {
                    // ── Level 9: PTM sites (per-type colour), variants (red
                    //    spheres, like Level 3), or enrichment (heatmap, like L2/4) ──
                    cartoonRefStore.current = cartoon;
                    console.log(positions, chain)
                    try {
                        if (colorBy === 'ptm' && datatable && datatable.length > 0) {
                            await applyPtmColoring(plugin, cartoon, datatable);
                        } else if (positions?.length && chain?.length) {
                            await applyLevel3Variants(plugin, cartoon, positions, chain);
                        } else if (datatable && datatable.length > 0) {
                            await applyLevel2Coloring(plugin, cartoon, datatable, centralityMetric);
                        }
                    } catch (coloringError) {
                        // Structure itself loaded fine; don't let a bad selection
                        // (e.g. residue ids that don't match this structure) abort
                        // structureLoadedRef below and break later filter updates.
                        console.warn('Level 9 coloring failed:', coloringError);
                    }
                }

                structureLoadedRef.current = true;

            } catch (error) {
                console.error('Failed to load the PDB structure:', error);
            }
        };

        init();

        return () => {
            cancelled = true;
            if (pluginInstanceRef.current) {
                pluginInstanceRef.current.dispose();
                pluginInstanceRef.current = null;
                structureLoadedRef.current = false;
                cartoonRefStore.current = null;
                overlayRefStore.current = null;
                labelReprRef.current = null;
            }
        };
    }, [pdbId, uniac, level, afId]);


    useEffect(() => {
        if (level !== "2" && level !== "9") return;
        if (!datatable || datatable.length === 0) return;
        if (!pluginInstanceRef.current || !structureLoadedRef.current) return;

        if (colorBy === 'ptm') {
            applyPtmColoring(pluginInstanceRef.current, cartoonRefStore.current, datatable);
        } else {
            applyLevel2Coloring(pluginInstanceRef.current, cartoonRefStore.current, datatable, centralityMetric);
        }
    }, [centralityMetric, datatable, level, colorBy]);

    // Level 3: re-apply variant spheres whenever the lateral filter changes
    // positions or chain. Without this, the spheres stay frozen because the
    // main useEffect doesn't watch these props.
    useEffect(() => {
        if (level !== "3" && level !== "9") return;
        if (!pluginInstanceRef.current || !structureLoadedRef.current) return;
        if (!cartoonRefStore.current) return;

        applyLevel3Variants(
            pluginInstanceRef.current,
            cartoonRefStore.current,
            positions,
            chain
        );
    }, [positions, chain, level]);

    // Level 5: re-colour the model when the colour mode (pLDDT/class) or the
    // datatable changes, without reloading the structure.
    useEffect(() => {
        if (level !== "5" && level !== "14") return;
        if (!pluginInstanceRef.current || !structureLoadedRef.current) return;
        if (!cartoonRefStore.current) return;
        if (level === "14") {
            if (colorBy === 'rewiring' && datatable && datatable.length > 0) {
                applyRewiringColoring(pluginInstanceRef.current, cartoonRefStore.current, datatable);
            } else if (datatable && datatable.length > 0) {
                applyClassColoring(pluginInstanceRef.current, cartoonRefStore.current, datatable);
            }
            return;
        }
        if (colorBy === 'class' && datatable && datatable.length > 0) {
            applyClassColoring(pluginInstanceRef.current, cartoonRefStore.current, datatable);
        } else {
            applyLevel5Plddt(pluginInstanceRef.current, cartoonRefStore.current);
        }
    }, [colorBy, datatable, level]);

    // Level 5: toggle the PDB superposition overlay. Loads it when `superpose`
    // turns on (storing the overlay's state ref) and removes that subtree when
    // it turns off — so the button can undo the superposition.
    useEffect(() => {
        const plugin = pluginInstanceRef.current;
        if ((level !== "5" && level !== "14") || !plugin || !structureLoadedRef.current) return;
        const overlayId = level === "14" ? overlayPdbId : pdbId;  // L14 overlays structure B
        let cancelled = false;
        (async () => {
            try {
                if (superpose && overlayId && !overlayRefStore.current) {
                    const ref = await loadPdbForSuperpositionTrigger(plugin, overlayId);
                    if (!cancelled) overlayRefStore.current = ref || null;
                } else if (!superpose && overlayRefStore.current) {
                    await plugin.build().delete(overlayRefStore.current).commit();
                    overlayRefStore.current = null;
                }
            } catch (e) {
                console.warn('Superposition toggle failed:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [superpose, level, pdbId, overlayPdbId]);

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '400px' }}>
            <div key="calculation" ref={viewerRef} style={{ width: '100%', height: '100%' }} />
            {level === "1" && (
                <button
                    type="button"
                    onClick={() => setShowLabels(v => !v)}
                    onMouseEnter={() => setLabelsBtnHover(true)}
                    onMouseLeave={() => setLabelsBtnHover(false)}
                    title="Residue labels on the path"
                    style={{
                        ...MSP_BUTTON_STYLE,
                        // same hover treatment as the skin: inset 1px outline
                        outline: labelsBtnHover ? '1px solid #C9C4B2' : 'none',
                        outlineOffset: -1,
                    }}
                >
                    {showLabels ? 'Hide labels' : 'Show labels'}
                </button>
            )}
        </div>
    );
};

export default MolstarViewerPath;
